import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { apiClient } from '../../../services/api';
import { OrganizationQuotaDetails } from '../../../types/domain';

export function useHomeData() {
  const navigate = useNavigate();
  const {
    user,
    organizations,
    refreshOrganizations,
    switchOrganization,
    createOrganization,
  } = useAuth();
  const { showToast } = useToast();

  // Create Organization Modal State
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);

  // Upgrade Plan Modal State
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [selectedOrgIdForUpgrade, setSelectedOrgIdForUpgrade] = useState<string>('');
  const [selectedTier, setSelectedTier] = useState<'free' | 'pro' | 'enterprise'>('pro');
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Organization quotas for genuine aggregated numbers
  const [quotasByOrg, setQuotasByOrg] = useState<Record<string, OrganizationQuotaDetails>>({});

  useEffect(() => {
    if (organizations.length === 0) return;
    Promise.all(
      organizations.map((org) =>
        apiClient<OrganizationQuotaDetails>(`/organizations/${org.id}/quota`)
          .then((q) => ({ orgId: org.id, quota: q }))
          .catch(() => null)
      )
    ).then((results) => {
      const map: Record<string, OrganizationQuotaDetails> = {};
      results.forEach((item) => {
        if (item && item.quota) {
          map[item.orgId] = item.quota;
        }
      });
      setQuotasByOrg(map);
    });
  }, [organizations]);

  // Aggregated global metrics across all organizations
  const totalProjects = useMemo(() => {
    return organizations.reduce((acc, org) => acc + (org.project_count || 0), 0);
  }, [organizations]);

  const totalDatastores = useMemo(() => {
    return organizations.reduce((acc, org) => {
      const quota = quotasByOrg[org.id];
      const count = quota?.tables?.used ?? 0;
      return acc + count;
    }, 0);
  }, [organizations, quotasByOrg]);

  const totalSubmissionsThisMonth = useMemo(() => {
    return organizations.reduce((acc, org) => {
      const quota = quotasByOrg[org.id];
      const count = quota?.submissions?.used ?? 0;
      return acc + count;
    }, 0);
  }, [organizations, quotasByOrg]);

  const totalStorageUsed = useMemo(() => {
    return organizations.reduce((acc, org) => {
      const quota = quotasByOrg[org.id];
      return acc + (quota?.storage?.used_bytes ?? 0);
    }, 0);
  }, [organizations, quotasByOrg]);

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;

    setIsCreatingOrg(true);
    try {
      const res = await createOrganization(newOrgName.trim());
      showToast({
        type: 'success',
        title: 'Organization Created',
        message: `Organization "${res.organization.name}" is ready.`,
      });
      setNewOrgName('');
      setCreateOrgOpen(false);
      navigate(`/organization/${res.organization.id}`);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Create Organization',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsCreatingOrg(false);
    }
  };

  const handleUpgradeSubscription = async () => {
    const targetOrgId =
      selectedOrgIdForUpgrade || (organizations.length > 0 ? organizations[0].id : '');
    if (!targetOrgId) return;

    setIsUpgrading(true);
    try {
      await apiClient(`/organizations/${targetOrgId}/subscription`, {
        method: 'POST',
        body: JSON.stringify({ plan: selectedTier }),
      });
      await refreshOrganizations();
      showToast({
        type: 'success',
        title: 'Subscription Updated',
        message: `Organization plan upgraded to ${selectedTier.toUpperCase()}.`,
      });
      setUpgradeModalOpen(false);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Upgrade Failed',
        message: err?.message || 'Could not update plan.',
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  const openUpgradeModal = (orgId?: string) => {
    const targetId = orgId || (organizations.length > 0 ? organizations[0].id : '');
    setSelectedOrgIdForUpgrade(targetId);
    const targetOrg = organizations.find((o) => o.id === targetId);
    if (targetOrg?.plan_tier === 'pro') {
      setSelectedTier('enterprise');
    } else if (targetOrg?.plan_tier === 'enterprise') {
      setSelectedTier('enterprise');
    } else {
      setSelectedTier('pro');
    }
    setUpgradeModalOpen(true);
  };

  const canUserAdminOrg = (roleStr?: string) => {
    return Boolean(user?.is_superadmin || roleStr === 'owner' || roleStr === 'admin');
  };

  const currentSelectedOrgForModal = useMemo(() => {
    return organizations.find((o) => o.id === selectedOrgIdForUpgrade) || organizations[0];
  }, [organizations, selectedOrgIdForUpgrade]);

  return {
    user,
    organizations,
    quotasByOrg,
    totalProjects,
    totalDatastores,
    totalSubmissionsThisMonth,
    totalStorageUsed,
    createOrgOpen,
    setCreateOrgOpen,
    newOrgName,
    setNewOrgName,
    isCreatingOrg,
    handleCreateOrg,
    upgradeModalOpen,
    setUpgradeModalOpen,
    selectedOrgIdForUpgrade,
    setSelectedOrgIdForUpgrade,
    selectedTier,
    setSelectedTier,
    isUpgrading,
    openUpgradeModal,
    handleUpgradeSubscription,
    canUserAdminOrg,
    currentSelectedOrgForModal,
    switchOrganization,
  };
}
